import { Fragment, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Button from "./Button";

/**
 * Trigger plus overflow menu. Locked entries are not rendered at all: their
 * reason belongs next to the object they apply to, not as a greyed-out row the
 * user can only guess at.
 *
 * An item is either an action (`onClick`) or a link (`href`, optional `target`).
 * A link stays a real anchor so it keeps middle-click and open-in-new-tab.
 * An item with `heading: true` is a non-clickable label that starts a group: the items after it,
 * up to the next heading, are announced as that group and later groups get a divider.
 *
 * The list is portalled to the body and positioned against the trigger. Inside a
 * table it would otherwise be clipped: `.table-responsive` scrolls horizontally,
 * and a box that scrolls on one axis cannot let the other overflow, so on a short
 * table the menu was cut off a few pixels below the row.
 */
export default function ActionMenu({
  items = [],
  label = "Actions",
  className = "",
  testIdPrefix = "",
  triggerLabel = null,
  triggerIcon = null,
  triggerVariant = "secondary",
  triggerSize,
  loading = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const menuId = useId();

  const place = useCallback(() => {
    const trigger = containerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    // Below the trigger unless it would not fit, then above. The list is fixed, so a menu
    // past the fold is unreachable rather than merely scrolled off: it does not extend the
    // document, and any scroll closes it. Height is 0 on the first pass because the list is
    // not mounted yet; the effect below runs again once it is.
    const height = listRef.current?.offsetHeight || 0;
    const fitsBelow = rect.bottom + 6 + height <= window.innerHeight;

    setPosition({
      top: fitsBelow ? rect.bottom + 6 : Math.max(6, rect.top - 6 - height),
      right: window.innerWidth - rect.right,
    });
  }, []);

  // position?.top in the deps so the measuring pass gets a second run; place() is idempotent
  // once the list is mounted, so it settles after that one.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, place, position?.top]);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutside = (event) => {
      const insideTrigger = containerRef.current && containerRef.current.contains(event.target);
      const insideList = listRef.current && listRef.current.contains(event.target);
      if (!insideTrigger && !insideList) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    // the menu is anchored to a rect, not to the row, so anything that moves the row closes it
    const closeOnMove = () => setOpen(false);

    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", closeOnMove, true);
    window.addEventListener("resize", closeOnMove);

    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", closeOnMove, true);
      window.removeEventListener("resize", closeOnMove);
    };
  }, [open]);

  if (items.filter((item) => !item.heading).length === 0) return null;

  const groups = items.reduce((all, item) => {
    if (item.heading) return [...all, { heading: item, items: [] }];
    if (all.length === 0) return [{ heading: null, items: [item] }];
    all[all.length - 1].items.push(item);
    return all;
  }, []);

  const renderItem = (item) => (
    item.href ? (
      <a
        role="menuitem"
        key={item.key}
        className={`peb-actionmenu__item ${item.danger ? "peb-actionmenu__item--danger" : ""}`}
        data-testid={`${testIdPrefix}action-menu-item-${item.key}`}
        href={item.href}
        target={item.target}
        rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
        onClick={() => setOpen(false)}
      >
        {item.label}
      </a>
    ) : (
      <button
        type="button"
        role="menuitem"
        key={item.key}
        className={`peb-actionmenu__item ${item.danger ? "peb-actionmenu__item--danger" : ""}`}
        data-testid={`${testIdPrefix}action-menu-item-${item.key}`}
        onClick={() => {
          setOpen(false);
          item.onClick();
        }}
      >
        {item.label}
      </button>
    )
  );

  const list = (
    <div
      className="peb-actionmenu__list"
      role="menu"
      ref={listRef}
      style={{ position: "fixed", top: position?.top, right: position?.right }}
      data-testid={`${testIdPrefix}action-menu-list`}
    >
      {groups.map((group, index) => (
        group.heading ? (
          <div
            key={group.heading.key}
            role="group"
            aria-labelledby={`${menuId}-${group.heading.key}`}
            className={`peb-actionmenu__group ${index > 0 ? "peb-actionmenu__group--divided" : ""}`}
            data-testid={`${testIdPrefix}action-menu-group-${group.heading.key}`}
          >
            <div id={`${menuId}-${group.heading.key}`} className="peb-actionmenu__heading" role="presentation">
              {group.heading.label}
            </div>
            {group.items.map(renderItem)}
          </div>
        ) : (
          <Fragment key={`ungrouped-${index}`}>{group.items.map(renderItem)}</Fragment>
        )
      ))}
    </div>
  );

  return (
    <div className={`peb-actionmenu ${className}`} ref={containerRef} data-testid={`${testIdPrefix}action-menu`}>
      {triggerLabel ? (
        <Button
          variant={triggerVariant}
          size={triggerSize}
          icon={triggerIcon}
          loading={loading}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={label}
          onClick={() => setOpen((was) => !was)}
          data-testid={`${testIdPrefix}action-menu-trigger`}
        >
          {triggerLabel}
          {!loading && <i className="bi bi-chevron-down peb-actionmenu__chevron" aria-hidden="true" />}
        </Button>
      ) : (
        <button
          type="button"
          className="peb-actionmenu__trigger"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={label}
          onClick={() => setOpen((was) => !was)}
          data-testid={`${testIdPrefix}action-menu-trigger`}
        >
          <i className="bi bi-three-dots" aria-hidden="true" />
        </button>
      )}
      {open && position && createPortal(list, document.body)}
    </div>
  );
}
